import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import { useSelector } from 'react-redux';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Typography, Button, Box, Divider, TextField } from '@mui/material';
import { IDeal, IPurchaserInfo, IDealRealtor, IDealDeposit } from '../../types/CreateDealForm';
import { FlexEnd } from '../../commonStyles';
import { selectProject } from '../../features/project/projectSlice';
import CommentsTable from '../common/CommentsTable';
import { IComment, ICommentArray } from '../../types/comment';
import { numToCurrency, numberWithCommas, convertAllDates, camelToTitle } from '../../utils/Functions';
import StandardTable from '../tables/StandardTable';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import BasicTabs from '../common/BasicTabs';
import Calls from './Calls';
import Texts from './Texts';
import Email from './Email';
import { selectUser } from '../../features/auth/authSlice';

const OutstandingProfile = (props: ChildProps) => {
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const storeDispatch = useAppDispatch();
  const { deals, realtor, updateDeal } = props;
  const [value, setValue] = useState<number>(0);
  const [subValue, setSubValue] = useState<number>(0);
  const [comments, setComments] = useState<IComment[]>([]);
  const [deal, setDeal] = useState<IDeal | null>(null);
  const [allNotes, setAllNotes] = useState<boolean>(false);
  const [note, setNote] = useState<string>('');

  useEffect(() => {
    if (deals.length === 1) {
      getComments({ variables: { filter: { unit: deals[0].unit?._id, project: project._id } } });
      setDeal(deals[0]);
    }
  }, [deals]);

  useEffect(() => {
    if (deal) {
      getComments({ variables: { filter: { unit: deal.unit?._id, project: project._id } } });
    }
  }, [deal]);

  const [callRegistrant, { loading }] = useLazyQuery(CALLREGISTRANT, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Calling...'));
    },
    onError: (e) => {
      console.log(e);
    },
  });

  const [createComments] = useMutation(CREATENOTES, {
    onCompleted: (data) => {
      setAllNotes(false);
      setDeal(null);
      setValue(0);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getComments] = useLazyQuery<ICommentArray>(GETCOMMENTS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      let sortedComments = [...data.commentMany].sort((a: any, b: any) => {
        if (b.pinned - a.pinned !== 0) {
          return b.pinned - a.pinned;
        } else return new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf();
      });
      setComments(sortedComments);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const columns = useMemo(() => {
    const viewMortgage = (url: string) => {
      const element = document.createElement('a');
      element.href = url;
      element.target = '_blank';
      element.click();
    };
    let allColumns = [
      {
        Header: 'Suite',
        accessor: (rowData: any) => {
          return (
            <Link
              style={{ textDecoration: 'none', color: 'inherit' }}
              rel="noopener noreferrer"
              target="_blank"
              to={`/${rowData.project._id}/dashboard/${rowData.unit._id}`}
            >
              <strong>
                {rowData.unit.suite} - {rowData.unit.status}
              </strong>
            </Link>
          );
        },
      },
      {
        Header: 'Purchaser',
        accessor: (rowData: any) => {
          return rowData.purchasers.map((purchaser: IPurchaserInfo, index: number) => {
            return (
              <Box>
                <strong>{purchaser.fullName}</strong>
              </Box>
            );
          });
        },
      },
      {
        Header: 'Mortgage',
        accessor: (rowData: any) => {
          if (
            (rowData.mortgage.amount && rowData.mortgage.getUrl && rowData.mortgage.lender && !rowData.mortgage.revisions.length) ||
            rowData.mortgage.revisions.includes('No MPA Required')
          ) {
            return <CheckIcon color="success" />;
          } else {
            let currentDeposit = rowData.deposit;
            if (rowData.pendingDeposits.length) {
              currentDeposit = rowData.pendingDeposits;
            }
            let totalDeposits = currentDeposit.reduce((a: any, b: any) => {
              return a + b.amount;
            }, 0);
            return (
              <Box>
                <Box
                  sx={{ fontWeight: rowData.mortgage.getUrl ? 700 : 500, cursor: rowData.mortgage.getUrl ? 'pointer' : 'auto' }}
                  onClick={() => {
                    if (rowData.mortgage.getUrl) {
                      viewMortgage(rowData.mortgage.getUrl);
                    }
                  }}
                >{`$${numberWithCommas(rowData.totalPrice - totalDeposits)}`}</Box>
                <Box>
                  {rowData.mortgage.revisions.map((revision: string, index: number) => {
                    return (
                      <Box key={index} sx={{ fontSize: '10px' }}>
                        <strong>{revision}</strong>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          }
        },
      },
      {
        Header: 'Deposits',
        accessor: (rowData: any) => {
          let currentDeposit = rowData.deposit;
          if (rowData.pendingDeposits.length) {
            currentDeposit = rowData.pendingDeposits;
          }
          return (
            <Box>
              {rowData.pendingDeposits.length ? (
                <Box>
                  <strong>PENDING DEPOSITS</strong>
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {currentDeposit.map((deposit: IDealDeposit, index: number) => {
                  let outstandingDeposit =
                    deposit.name !== 'Occupancy' &&
                    deposit.name !== 'Occupancy Deposit' &&
                    (deposit.chequeType === '' || deposit.chequeType === 'nsf' || deposit.chequeType === 'replacement') &&
                    new Date().valueOf() > new Date(deposit.dueDate!).valueOf()
                      ? true
                      : false;
                  return (
                    <Box key={index} sx={{ fontSize: '10px', cursor: 'pointer' }}>
                      <strong>{deposit.name}</strong> - {numToCurrency.format(deposit.amount!)} -{' '}
                      <Box sx={{ display: 'inline-block', color: outstandingDeposit ? 'red' : '#000' }}>
                        {convertAllDates(deposit.dueDate, 'PP')}
                      </Box>{' '}
                      - {deposit.chequeType ? camelToTitle(deposit.chequeType!) : 'Not Received'}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        },
      },
      {
        Header: 'Documents',
        accessor: (rowData: any) => {
          return rowData.documents.map((document: any) => {
            let outstandingDocument = document.status !== 'Completed' ? true : false;
            return (
              <Box sx={{ fontSize: '10px' }}>
                {document.name} - <Box sx={{ display: 'inline-block', color: outstandingDocument ? 'red' : '#000' }}>{document.status}</Box>
              </Box>
            );
          });
        },
      },
    ];

    if (realtor && deals.length > 1) {
      allColumns.push({
        Header: 'Deal',
        accessor: (rowData: any) => {
          return (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setValue(0);
                setAllNotes(false);
                setDeal(rowData);
              }}
            >
              View
            </Button>
          );
        },
      });
    }

    return allColumns;
  }, [deals]);

  const textTabs = () => {
    return deal?.purchasers.map((purchaser: IPurchaserInfo) => {
      return {
        label: `${purchaser.fullName}`,
        component: <Texts email={purchaser.email} phone={purchaser.primaryPhone} type="purchaser" deal={deal!} />,
      };
    });
  };

  const callTabs = () => {
    return deal?.purchasers.map((purchaser: IPurchaserInfo) => {
      return {
        label: `${purchaser.fullName}`,
        component: <Calls email={purchaser.email} phone={purchaser.primaryPhone} type="purchaser" />,
      };
    });
  };

  const textView = () => {
    if (realtor) {
      return (
        <Box>
          <Typography variant="h3" sx={{ mb: 2 }}>
            <strong>{realtor.fullName} SMS Log</strong>
          </Typography>
          <Texts email={realtor.email!} phone={realtor.directPhone!} type="realtor" deal={deal!} />
        </Box>
      );
    } else {
      return <BasicTabs tabs={textTabs()!} sub={true} value={subValue} setValue={setSubValue} color={'#fff'} />;
    }
  };

  const callView = () => {
    if (realtor) {
      return (
        <Box>
          <Typography variant="h3" sx={{ mb: 2 }}>
            <strong>{realtor.fullName} Call Log</strong>
          </Typography>
          <Calls email={realtor.email!} phone={realtor.directPhone!} type="realtor" />
        </Box>
      );
    } else {
      return <BasicTabs tabs={callTabs()!} sub={true} value={subValue} setValue={setSubValue} color={'#fff'} />;
    }
  };

  const thirdButton = () => {
    return (
      <Button sx={{ ml: 1 }} variant="contained" onClick={() => setAllNotes(!allNotes)}>
        Create Note For All Deals
      </Button>
    );
  };

  const tabs = [
    {
      label: 'Notes',
      component: (
        <CommentsTable
          deal={deal!}
          unit={deal?.unit}
          commentType={'deal'}
          comments={comments.filter((comment: IComment) => comment.deal && comment.deal._id === deal?._id)}
          setComments={setComments}
          thirdButton={realtor ? thirdButton : null}
        />
      ),
    },
    {
      label: 'Calls',
      component: callView(),
    },
    {
      label: 'Texts',
      component: textView(),
    },
    {
      label: 'Email',
      component: <Email deal={deal!} realtor={realtor} deals={deals} />,
    },
  ];

  const handleCallRegistrant = (phone: string) => {
    if (!phone) return storeDispatch(showErrorSnackbar('Purchaser does not have a phone number'));
    let formattedPhone = phone.replace(/\D/g, '');
    if (formattedPhone.length === 10 || formattedPhone.length === 11) {
      callRegistrant({
        variables: {
          project: project._id,
          customerPhone: parseInt(formattedPhone, 10),
          callType: 'project',
        },
      }).then((res) => {});
    } else return storeDispatch(showErrorSnackbar('Invalid Phone Number'));
  };

  const submitNote = () => {
    if (!note.length) return storeDispatch(showErrorSnackbar('No Note'));

    let comments = [];
    for (const deal of deals) {
      comments.push({
        project: project._id,
        unit: deal.unit?._id,
        user: user._id,
        comment: note,
        type: 'general',
        deal: deal._id,
        pinned: false,
        deleted: false,
      });
    }
    createComments({ variables: { records: comments } });
  };

  return (
    <div>
      <Typography variant="h3" sx={{ mb: 2 }}>
        {deals.length === 1 ? (
          <strong>
            {deals[0].unit.suite} - {deals[0].purchasers.map((purchaser: IPurchaserInfo) => purchaser.fullName).join(' & ')}
          </strong>
        ) : (
          <strong>
            {realtor?.fullName} {deal ? `- Suite ${deal.unit.suite}` : null}
          </strong>
        )}
      </Typography>
      <StandardTable columns={columns} data={deals} />
      {deal && !allNotes ? (
        <Box sx={{ mt: 2 }}>
          <BasicTabs tabs={tabs} sub={true} value={value} setValue={setValue} color={'#fff'} />
        </Box>
      ) : null}
      {allNotes ? (
        <Box>
          <TextField
            sx={{ mt: 2 }}
            fullWidth
            required
            label={`Note to add to all ${realtor?.fullName} deals`}
            variant="outlined"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <FlexEnd>
            <Button sx={{ mt: 2 }} onClick={() => submitNote()} variant="contained" color="success">
              Add to all deals
            </Button>
          </FlexEnd>
        </Box>
      ) : null}
      <Divider sx={{ mt: 2 }} />
      <FlexEnd sx={{ mt: 2 }}>
        {deals.length === 1 && !realtor ? (
          deals[0].purchasers.map((purchaser: IPurchaserInfo, index: number) => {
            return (
              <Button
                key={index}
                sx={{ mr: 1 }}
                variant="contained"
                onClick={() => {
                  handleCallRegistrant(purchaser.primaryPhone);
                }}
              >
                Call {purchaser.firstName}
              </Button>
            );
          })
        ) : (
          <Button
            sx={{ mr: 1 }}
            variant="contained"
            onClick={() => {
              handleCallRegistrant(realtor?.directPhone!);
            }}
          >
            Call {realtor?.fullName}
          </Button>
        )}
        {deal ? (
          <>
            {/* <Button sx={{ mr: 1 }} variant="contained" onClick={() => setValue(2)}>
              Text
            </Button> */}
            <Button sx={{ mr: 1 }} variant="contained" onClick={() => setValue(3)}>
              Email
            </Button>
          </>
        ) : null}
        {deals.length === 1 ? (
          <>
            {["escalate", "doNotTouch", "default", "extension"].map((type: string) => {
              return (
                <Button
                  sx={{ mr: 1 }}
                  variant="contained"
                  color={deals[0].type === type ? 'success' : 'error'}
                  onClick={() => {
                    updateDeal({
                      variables: {
                        _id: deals[0]._id,
                        record: {
                          type: deals[0].type === type ? '' : type
                        },
                      },
                    });
                  }}
                >
                  {deals[0].type === type ? 'Normal' : camelToTitle(type)}
                </Button>
              )
            })}
          </>
        ) : null}
      </FlexEnd>
    </div>
  );
};

interface ChildProps {
  deals: IDeal[];
  realtor: IDealRealtor | null;
  updateDeal: any;
}

const GETCOMMENTS = gql`
  query commentMany($filter: FilterFindManyCommentInput) {
    commentMany(filter: $filter) {
      _id
      user {
        _id
        fullName
      }
      deal {
        _id
      }
      comment
      type
      pinned
      createdAt
      updatedAt
    }
  }
`;

const CALLREGISTRANT = gql`
  query callRegistrant($project: MongoID, $customerPhone: Float!, $callType: String!) {
    callRegistrant(project: $project, customerPhone: $customerPhone, callType: $callType)
  }
`;

const CREATENOTES = gql`
  mutation commentCreateMany($records: [CreateManyCommentInput!]!) {
    commentCreateMany(records: $records) {
      records {
        _id
        user {
          _id
          fullName
        }
        deal {
          _id
        }
        comment
        type
        pinned
        createdAt
        updatedAt
      }
    }
  }
`;

export default OutstandingProfile;
